import { type EditorHandle, setEditorColorScheme } from "@codeshare/editor";
import type { GoFeatures } from "@codeshare/go-tools";
import { languages } from "@codeshare/languages";
import {
  DEFAULT_OCAML_COMPILER,
  isOcamlCompiler,
  OCAML_COMPILERS,
} from "@codeshare/runner-ocaml/version";
import { type DragEvent, lazy, Suspense, useEffect, useRef, useState } from "react";
import { useChat } from "./chat/use-chat";
import { appCommands, MENUS } from "./commands/app-commands";
import { AboutDialog } from "./components/AboutDialog";
import { ActivityBar } from "./components/ActivityBar";
import { CodeEditor } from "./components/CodeEditor";
import { CommandPalette, type PaletteState } from "./components/CommandPalette";
import { ConfirmHost, confirmAction } from "./components/ConfirmDialog";
import { EditorActions } from "./components/EditorActions";
import { FileIcon } from "./components/FileIcon";
import { FileName } from "./components/FileName";
import { InsecureBanner } from "./components/InsecureBanner";
import { ReadOnlyBanner } from "./components/ReadOnlyBanner";
import { SidePanel } from "./components/SidePanel";
import { StatusBar } from "./components/StatusBar";
import { Tabs } from "./components/Tabs";
import type { TerminalTab } from "./components/TerminalPanel";
import { TopBar } from "./components/TopBar";
import { UpdateBanner } from "./components/UpdateBanner";
import { VersionBanner } from "./components/VersionBanner";
import { looksBinary } from "./document/files";
import { useColorScheme } from "./hooks/use-color-scheme";
import { useEditorStatus } from "./hooks/use-editor-status";
import { matches, NARROW_QUERY, useMedia } from "./hooks/use-media";
import { SHORTCUTS, useShortcuts } from "./hooks/use-shortcuts";
import { SOURCE_URL } from "./links";
import { isTestFile } from "./run/code-lens";
import { installEditorRuns } from "./run/editor-runs";
import { loadPinnedEntry, savePinnedEntry } from "./run/pinned-entry";
import { cannotRun } from "./run/run-session";
import { getRunSettings, PACKAGE_CDN } from "./run/run-settings";
import { runnerFor } from "./run/runners";
import { useRun, useRunSessions, useTerminalSharing, useTestResults } from "./run/use-run";
import { useRoom } from "./session/use-room";
import {
  candidatesFromDrop,
  candidatesFromFileList,
  describeSkipped,
  prepareImport,
} from "./workspace/import-export";
import { useWorkspace } from "./workspace/use-workspace";

// The explorer's tree library (with its icon set) is its own chunk, fetched right after startup.
const FileTree = lazy(() => import("./components/FileTree").then((m) => ({ default: m.FileTree })));

// The terminal (xterm and the stream protocol) loads the first time it's opened.
const TerminalPanel = lazy(() =>
  import("./components/TerminalPanel").then((m) => ({ default: m.TerminalPanel })),
);

export function App() {
  const { scheme, setPreference } = useColorScheme();
  const [palette, setPalette] = useState<PaletteState>();
  const [aboutOpen, setAboutOpen] = useState(false);
  const [editorHandle, setEditorHandle] = useState<EditorHandle>();
  const editorStatus = useEditorStatus(editorHandle);
  const doc = useWorkspace();
  const narrow = useMedia(NARROW_QUERY);
  // Go language features (format, diagnostics, completion, hover) load with the first Go file,
  // then stay (switching to another file and back shouldn't reload them).
  const [goNeeded, setGoNeeded] = useState(false);
  useEffect(() => {
    if (doc.language.id === "go") setGoNeeded(true);
  }, [doc.language.id]);
  // Resolves once the Go features are registered (⌘S waits for it on the first save).
  const goFeatures = useRef<Promise<GoFeatures>>(undefined);
  useEffect(() => {
    if (!goNeeded || !editorHandle) return;
    let features: GoFeatures | undefined;
    let cancelled = false;
    goFeatures.current = import("@codeshare/go-tools").then(({ registerGoFeatures }) => {
      features = registerGoFeatures(editorHandle.monaco, { files: () => doc.session.contents() });
      if (cancelled) features.dispose();
      return features;
    });
    return () => {
      cancelled = true;
      features?.dispose();
      goFeatures.current = undefined;
    };
  }, [goNeeded, editorHandle, doc.session]);
  // On phones the file tree is a drawer: closed until asked for.
  const [panels, setPanels] = useState(() => ({
    side: false,
    bottom: false,
    files: !matches(NARROW_QUERY),
  }));
  const [dragging, setDragging] = useState(false);
  const room = useRoom();
  const { state: chat } = useChat();
  const { run, status: runStatus } = useRun();
  const sessions = useRunSessions();
  const { sharing, state: sharingState } = useTerminalSharing();
  const [terminalTab, setTerminalTab] = useState<TerminalTab>("local");
  // Other people's sessions whose stream reached us.
  const mirrors = sessions.flatMap((session) => {
    const channel = sharingState.mirrors.includes(session.id)
      ? sharing.mirrorChannel(session.id)
      : undefined;
    return channel ? [{ session, channel }] : [];
  });
  const watch = (sessionId: string) => {
    setTerminalTab(sessionId);
    setPanels((p) => ({ ...p, bottom: true }));
  };
  // What ▷ runs: the pinned entry point ("Always run …", per workspace, on this device) while it
  // exists, else the open file. Tests are always the open file's (its package or project).
  const workspaceKey = doc.session.topic ?? "local";
  const [pinned, setPinned] = useState(() => loadPinnedEntry(workspaceKey));
  useEffect(() => setPinned(loadPinnedEntry(workspaceKey)), [workspaceKey]);
  const pin = (path: string | undefined) => {
    savePinnedEntry(workspaceKey, path);
    setPinned(path);
  };
  const pinnedLive = pinned && doc.files.some((f) => f.path === pinned) ? pinned : undefined;
  const target = pinnedLive ?? doc.name;
  const targetLanguage =
    target === doc.name ? doc.language.id : (languages.forPath(target)?.id ?? "plaintext");
  const canRun = cannotRun(targetLanguage);
  const runner = runnerFor(targetLanguage);
  const openRunner = runnerFor(doc.language.id);
  const cannotTest = openRunner?.tests
    ? undefined
    : `${languages.get(doc.language.id)?.name ?? "This file"} has no test runner here`;
  /** Runs the entry point, or (mode "test") the open file's tests, optionally just one. */
  const startRun = (mode: "run" | "test" = "run", test?: string, file?: string) => {
    const testing = mode === "test";
    // A specific file (a ▷ link in the editor), else tests: the open file; runs: the entry point.
    const entry = file ?? (testing ? doc.name : target);
    const languageId =
      entry === doc.name ? doc.language.id : (languages.forPath(entry)?.id ?? "plaintext");
    const entryRunner = runnerFor(languageId);
    if (!entryRunner || (testing && !entryRunner.tests)) return;
    setPanels((p) => ({ ...p, bottom: true }));
    setTerminalTab("local");
    // Let the terminal mount before output starts.
    requestAnimationFrame(() => {
      const files = Object.fromEntries(doc.session.contents().map((f) => [f.path, f.text]));
      const settings = getRunSettings();
      const args = entryRunner && !testing ? settings.argsFor(entryRunner.id) : [];
      const packages = entryRunner ? settings.packagesAllowed(entryRunner.id) : false;
      const toolchain = doc.toolchains[entryRunner.id];
      run.run({
        entry,
        languageId,
        files,
        args,
        ...(toolchain ? { toolchain } : {}),
        ...(packages ? { packageCdn: PACKAGE_CDN } : {}),
        ...(testing ? { mode: "test" as const, ...(test ? { test } : {}) } : {}),
      });
    });
  };
  const runActive = () => startRun("run");
  const runTests = (test?: string, file?: string) => startRun("test", test, file);

  // ▷ Run / ▷ Run test links and inline results in the editor.
  const testResults = useTestResults();
  const editorRuns = useRef<ReturnType<typeof installEditorRuns>>(undefined);
  const latestRun = useRef(startRun);
  latestRun.current = startRun;
  useEffect(() => {
    if (!editorHandle) return;
    const installed = installEditorRuns(editorHandle, {
      run: (path) => latestRun.current("run", undefined, path),
      test: (path, name) => latestRun.current("test", name, path),
    });
    editorRuns.current = installed;
    return () => {
      installed.dispose();
      editorRuns.current = undefined;
    };
  }, [editorHandle]);
  useEffect(() => editorRuns.current?.setResults(testResults), [testResults]);
  const [shareStatus, setShareStatus] = useState<string>();

  // Opening someone's room link shows who's there (not over the editor on phones, where it's a
  // sheet). Rooms that start on their own (every plain visit) stay out of the way.
  const [openedFromLink] = useState(() => location.hash.startsWith("#r="));
  useEffect(() => {
    if (openedFromLink && room.state.status === "joined" && !matches(NARROW_QUERY))
      setPanels((p) => ({ ...p, side: true }));
  }, [openedFromLink, room.state.status]);

  /** Copies the room link and shows the invite (people & chat), like Live Share. */
  const share = async () => {
    if (!matches(NARROW_QUERY)) setPanels((p) => ({ ...p, side: true }));
    const link = await room.share();
    try {
      await navigator.clipboard.writeText(link);
      setShareStatus("Link copied");
    } catch {
      setShareStatus("Link is in the address bar");
    }
    setTimeout(() => setShareStatus(undefined), 3_000);
  };

  useEffect(() => {
    setEditorColorScheme(scheme);
  }, [scheme]);

  const openFile = async (file: File) => {
    const text = await file.text();
    if (looksBinary(text)) {
      notify(`${file.name} looks like a binary file and can't be opened as text.`, 5_000);
      return;
    }
    doc.open(file.name, text);
  };

  const importCandidates = async (candidates: Awaited<ReturnType<typeof candidatesFromDrop>>) => {
    const { files, skipped } = await prepareImport(candidates);
    if (files.length > 0) doc.importFiles(files);
    const note = describeSkipped(skipped);
    if (note) setShareStatus(note);
  };

  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    setDragging(false);
    void candidatesFromDrop(event.dataTransfer).then(importCandidates);
  };

  /**
   * Opening a panel from its toggle moves focus into it; closing it (×, Escape, the toggle)
   * returns focus to the toggle, so keyboard users don't lose their place.
   */
  const togglePanel = (panel: "side" | "bottom" | "files") => {
    const opening = !panels[panel];
    setPanels((p) => ({ ...p, [panel]: opening }));
    // Once the panel has rendered (which can take more than a frame).
    const focus = (frames: number) => {
      const inPanel = (selector: string) =>
        document.querySelector<HTMLElement>(`[data-panel="${panel}"] ${selector}`);
      const target = opening
        ? (inPanel('[role="tab"][tabindex="0"]') ?? inPanel("button"))
        : document.getElementById(`toggle-${panel}`);
      if (target) target.focus();
      else if (frames > 0) requestAnimationFrame(() => focus(frames - 1));
    };
    requestAnimationFrame(() => focus(120));
  };

  /** Focuses an element once it exists (panels mount lazily). */
  const focusWhenReady = (selector: string, frames = 120) => {
    const element = document.querySelector<HTMLElement>(selector);
    if (element) element.focus();
    else if (frames > 0) requestAnimationFrame(() => focusWhenReady(selector, frames - 1));
  };
  // Like VS Code: the terminal takes focus when it opens, and the editor gets it back on close.
  const toggleTerminal = () => {
    const opening = !panels.bottom;
    setPanels((p) => ({ ...p, bottom: opening }));
    focusWhenReady(
      opening
        ? '[data-panel="bottom"] .terminal-host:not([hidden]) textarea'
        : ".monaco-editor textarea",
    );
  };
  const notify = (message: string, ms = 3_000) => {
    setShareStatus(message);
    setTimeout(() => setShareStatus(undefined), ms);
  };
  // "Ada joined" / "Ada left", like a notification.
  const readyPeers = useRef(new Map<string, string>());
  useEffect(() => {
    const now = new Map(
      room.state.peers
        .filter((peer) => peer.state === "ready")
        .map((peer) => [peer.id, peer.profile?.name ?? "Someone"] as const),
    );
    for (const [id, name] of now) if (!readyPeers.current.has(id)) notify(`${name} joined`);
    for (const [id, name] of readyPeers.current) if (!now.has(id)) notify(`${name} left`);
    readyPeers.current = now;
  });

  const showExplorer = () => {
    if (!panels.files) togglePanel("files");
    else focusWhenReady('[data-panel="files"] .tree');
  };
  /** Moves to the next (1) or previous (-1) tab, wrapping around. */
  const cycleEditor = (by: 1 | -1) => {
    const index = doc.files.findIndex((f) => f.id === doc.activeId);
    const next = doc.files[(index + by + doc.files.length) % doc.files.length];
    if (next) doc.select(next.id);
  };
  const changeLanguage = () =>
    setPalette({
      mode: "pick",
      label: "Select language mode",
      items: [
        { id: "auto", label: "Auto Detect", run: () => doc.setLanguageOverride(null) },
        ...[{ id: "plaintext", name: "Plain Text" }, ...languages.all()].map((lang) => ({
          id: lang.id,
          label: lang.name,
          run: () => doc.setLanguageOverride(lang.id),
        })),
      ],
    });
  // OCaml files run with the room's compiler: OCaml or OxCaml.
  const ocamlCompiler = isOcamlCompiler(doc.toolchains.ocaml)
    ? doc.toolchains.ocaml
    : DEFAULT_OCAML_COMPILER;
  const changeOcamlCompiler = () =>
    setPalette({
      mode: "pick",
      label: "Select the OCaml compiler (for everyone in the room)",
      items: Object.entries(OCAML_COMPILERS).map(([id, { name, version }]) => ({
        id,
        label: `${name} ${version}${id === ocamlCompiler ? " (current)" : ""}`,
        run: () => doc.setToolchain("ocaml", id === DEFAULT_OCAML_COMPILER ? undefined : id),
      })),
    });
  const editorAction = (id: string) => {
    const editor = editorHandle?.editor;
    if (!editor) return;
    editor.focus();
    editor.trigger("menu", id, null);
  };
  /** Clicks a control that may mount a moment later (e.g. after opening its panel). */
  const clickWhenReady = (selector: string, frames = 60) => {
    const element = document.querySelector<HTMLElement>(selector);
    if (element) element.click();
    else if (frames > 0) requestAnimationFrame(() => clickWhenReady(selector, frames - 1));
  };
  const commands = appCommands({
    readOnly: !doc.canWrite,
    hasFile: Boolean(doc.activeId),
    inRoom: room.state.status !== "solo",
    running: runStatus.kind === "running" || runStatus.kind === "loading",
    cannotRun: canRun,
    cannotTest,
    openPath: doc.name,
    pinned: pinnedLive,
    newFile: () => doc.newUntitled(),
    openFile: () => clickWhenReady('[data-testid="open-file-input"]'),
    importFolder: () => {
      if (!panels.files) setPanels((p) => ({ ...p, files: true }));
      clickWhenReady('[data-testid="import-folder-input"]');
    },
    rename: () => clickWhenReady('.breadcrumbs [title="Rename"]'),
    deleteFile: () => {
      const id = doc.activeId;
      if (!id) return;
      void confirmAction({
        title: `Delete ${doc.name}?`,
        detail: "It's deleted for everyone in the room.",
        confirmLabel: "Delete",
        danger: true,
      }).then((confirmed) => confirmed && doc.deleteFile(id));
    },
    download: doc.download,
    downloadAll: doc.downloadAll,
    editorAction,
    run: runActive,
    runTests: () => runTests(),
    pin,
    stop: () => run.stop(),
    runOptions: () => clickWhenReady(".run-options-button"),
    quickOpen: () => setPalette({ mode: "files" }),
    commandPalette: () => setPalette({ mode: "commands" }),
    showExplorer,
    togglePanel,
    toggleTerminal,
    cycleEditor,
    changeLanguage,
    changeOcamlCompiler,
    share: () => void share(),
    leave: () => void room.leave(),
    connectionSettings: () => clickWhenReady("#open-connection"),
    profile: () => clickWhenReady("#open-profile"),
    setTheme: setPreference,
    about: () => setAboutOpen(true),
    source: () => window.open(SOURCE_URL, "_blank", "noopener,noreferrer"),
  });

  useShortcuts([
    { ...SHORTCUTS.toggleTerminal, run: toggleTerminal },
    { ...SHORTCUTS.toggleTerminalAlt, run: toggleTerminal },
    { ...SHORTCUTS.toggleFiles, run: () => togglePanel("files") },
    { ...SHORTCUTS.togglePeople, run: () => togglePanel("side") },
    { ...SHORTCUTS.showExplorer, run: showExplorer },
    { ...SHORTCUTS.run, run: runActive },
    { ...SHORTCUTS.runTests, run: () => runTests() },
    { ...SHORTCUTS.quickOpen, run: () => setPalette({ mode: "files" }) },
    { ...SHORTCUTS.commandPalette, run: () => setPalette({ mode: "commands" }) },
    { ...SHORTCUTS.commandPaletteAlt, run: () => setPalette({ mode: "commands" }) },
    { ...SHORTCUTS.nextEditor, run: () => cycleEditor(1) },
    { ...SHORTCUTS.previousEditor, run: () => cycleEditor(-1) },
    // Nothing to save: every change is kept as you type. Don't show the browser's dialog.
    {
      ...SHORTCUTS.save,
      run: () => {
        // Like VS Code with the Go extension: saving formats (goimports). Everything else is
        // already kept as you type.
        const model = editorHandle?.editor.getModel();
        if (doc.language.id === "go" && doc.canWrite && model) {
          setGoNeeded(true);
          void (async () => {
            // The first save can come before the Go tools have loaded: wait for them.
            for (let i = 0; i < 50 && !goFeatures.current; i++)
              await new Promise((r) => setTimeout(r, 50));
            const error = await (await goFeatures.current)?.format(model);
            if (error) notify(`Couldn't format: ${error}`, 5_000);
          })();
        } else {
          notify("Nothing to save: changes save as you type", 2_000);
        }
      },
    },
  ]);

  return (
    <div className="app">
      <div className="banners">
        <InsecureBanner />
        <ReadOnlyBanner canWrite={doc.canWrite} />
        <VersionBanner room={room.state} />
        <UpdateBanner />
      </div>
      <TopBar
        title={doc.name}
        menus={MENUS}
        commands={commands}
        onOpenFile={(file) => void openFile(file)}
        onShare={() => void share()}
        onQuickOpen={() => setPalette({ mode: "files" })}
        peopleOpen={panels.side}
        onTogglePeople={() => togglePanel("side")}
        peers={room.state.peers.filter((peer) => peer.state === "ready").length}
        unread={chat.unread}
      />
      <div className="workspace">
        <ActivityBar
          panels={panels}
          onTogglePanel={togglePanel}
          onCommandPalette={() => setPalette({ mode: "commands" })}
          roomHasPeople={room.state.peers.length > 0}
          onConnectionSaved={() => {
            // Nobody has joined yet: start the room again with the new relays and servers.
            if (room.state.status === "solo" || room.state.peers.length > 0) return;
            void room.restart();
          }}
        />
        {panels.files && (
          <Suspense
            fallback={
              <nav className="file-tree" aria-label="Files" data-panel="files" aria-busy>
                <header className="panel-header">
                  <h2>Explorer</h2>
                </header>
              </nav>
            }
          >
            <FileTree
              files={doc.files}
              activeId={doc.activeId}
              readOnly={!doc.canWrite}
              {...(narrow ? { onClose: () => togglePanel("files") } : {})}
              onOpen={(id) => {
                doc.select(id);
                if (narrow) setPanels((p) => ({ ...p, files: false }));
              }}
              onCreate={(path) => doc.addFile(path)}
              onRename={doc.renameFile}
              onDelete={doc.deleteFile}
              onImport={(list) => void importCandidates(candidatesFromFileList(list))}
              onDownloadAll={doc.downloadAll}
            />
          </Suspense>
        )}
        <main
          className={`editor-area${dragging ? " dragging" : ""}`}
          data-testid="editor-area"
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
        >
          <div className="editor-title">
            <Tabs
              files={doc.files}
              activeId={doc.activeId}
              readOnly={!doc.canWrite}
              onSelect={doc.select}
              onMove={doc.moveFile}
            />
            <EditorActions
              target={target}
              pinned={Boolean(pinnedLive)}
              openFile={doc.name}
              openIsTest={isTestFile(
                doc.language.id,
                doc.name,
                // OCaml's tests can be in any file: whether this one has some.
                doc.language.id === "ocaml"
                  ? doc.session.workspace.findByPath(doc.name)?.text.toString()
                  : undefined,
              )}
              cannotRun={canRun}
              tests={openRunner?.tests}
              onRun={runActive}
              onTest={() => runTests()}
              onPin={pin}
              runner={runner}
            />
          </div>
          <div className="breadcrumbs">
            <FileIcon path={doc.name} />
            <FileName name={doc.name} onRename={doc.rename} />
          </div>
          <CodeEditor
            path="loading"
            onReady={(handle) => {
              doc.attach(handle);
              setEditorHandle(handle);
            }}
          />
          {panels.bottom && (
            <Suspense
              fallback={<section className="panel panel-bottom" aria-label="Terminal" aria-busy />}
            >
              <TerminalPanel
                status={runStatus}
                canRun={canRun}
                onRun={runActive}
                onClose={() => togglePanel("bottom")}
                mirrors={mirrors}
                tab={terminalTab}
                onTab={setTerminalTab}
              />
            </Suspense>
          )}
        </main>
        {panels.side && (
          <SidePanel
            room={room.state}
            onLeave={() => void room.leave()}
            onForget={() => void room.forget()}
            onClose={() => togglePanel("side")}
            watchable={mirrors.map((m) => m.session.id)}
            onWatch={watch}
          />
        )}
      </div>
      {/* Short-lived notices ("Link copied"): float above the layout instead of shifting it. */}
      <div className="toast-region">
        {shareStatus && (
          <span className="toast" role="status">
            {shareStatus}
          </span>
        )}
      </div>
      <StatusBar
        language={doc.language}
        editor={editorStatus}
        onGoToLine={() => editorAction("editor.action.gotoLine")}
        room={room.state}
        onShowRoom={() => !panels.side && togglePanel("side")}
        onChangeLanguage={changeLanguage}
        {...(doc.language.id === "ocaml"
          ? {
              toolchain: {
                label: OCAML_COMPILERS[ocamlCompiler].name,
                onChange: changeOcamlCompiler,
              },
            }
          : {})}
      />
      <AboutDialog open={aboutOpen} onClose={() => setAboutOpen(false)} />
      <ConfirmHost />
      <CommandPalette
        state={palette}
        files={doc.files}
        commands={commands}
        onOpenFile={doc.select}
        onClose={() => setPalette(undefined)}
      />
    </div>
  );
}
